import {
  type MessagePathPart,
  MessagePathPartType,
  type MessagePathSlice,
} from "@/shared/domain/topics/MessagePathRecord";
import { MessagePayload } from "@/shared/webworker";

import type {
  PointCloudFrame,
  PointCloudMapping,
} from "../worker/pointCloud/packedPointCloud";

export type {
  PointCloudAxisMapping,
  PointCloudFrame,
  PointCloudMapping,
} from "../worker/pointCloud/packedPointCloud";

/**
 * Generates a unique key for a message path entry, disambiguating
 * different array slices of the same field (which share the same id).
 */
export function messagePathKey(mp: {
  id: string;
  parts: MessagePathPart[];
}): string {
  const sliceSuffix = mp.parts
    .filter((p): p is MessagePathSlice => p.type === MessagePathPartType.Slice)
    .map(
      (p) =>
        `[${p.start}:${p.end !== null && p.end !== undefined && isFinite(p.end) ? p.end : ""}]`,
    )
    .join("");
  return sliceSuffix ? `${mp.id}::${sliceSuffix}` : mp.id;
}

export enum DataServerCommand {
  GetAllData = "get-all-data",
  GetLogTimes = "get-log-times",
  GetPointCloudAt = "get-point-cloud-at",
  Init = "init",
}

export enum DataServerCommandResponse {
  GetAllDataResponse = "get-all-data-response",
  GetLogTimesResponse = "get-log-times-response",
  GetPointCloudAtResponse = "get-point-cloud-at-response",
  InitResponse = "init-response",
}

export type TopicIdToMessagePathValues = Record<
  string,
  { logtimes: bigint[]; values: Record<string, number[]> }
>;

export type TopicIdToMessagePaths = Record<
  string,
  Record<string, { id: string; parts: MessagePathPart[] }>
>;

/** Asks for a topic's full log-time index, the thing to binary-search for "latest message at or before t". */
export interface GetLogTimesRequest {
  topicId: string;
}

export type GetLogTimesResponse = bigint[];

/** Asks for the one PointCloud message at `logTime`, decoded into scene-space vertices. */
export interface GetPointCloudAtRequest {
  topicId: string;
  /**
   * The cloud's `data` message path, not just any path of the topic: the
   * worker asks the reader for this path, as bytes, beside the layout columns,
   * so for any other path a Parquet read hands back no `data` to decode.
   */
  messagePathId: string;
  logTime: bigint;
  mapping: PointCloudMapping;
}

/** `undefined` when the topic has no message at that log time. */
export type GetPointCloudAtResponse = PointCloudFrame | undefined;

export interface DataServerCommandPayloadMap {
  [DataServerCommand.GetAllData]: TopicIdToMessagePaths;
  [DataServerCommand.GetLogTimes]: GetLogTimesRequest;
  [DataServerCommand.GetPointCloudAt]: GetPointCloudAtRequest;
  [DataServerCommand.Init]: undefined;
}

export interface DataServerCommandResponsePayloadMap {
  [DataServerCommandResponse.GetAllDataResponse]: TopicIdToMessagePathValues;
  [DataServerCommandResponse.GetLogTimesResponse]: GetLogTimesResponse;
  [DataServerCommandResponse.GetPointCloudAtResponse]: GetPointCloudAtResponse;
  [DataServerCommandResponse.InitResponse]: undefined;
}

export function isDataServerInitCommand(
  event: MessageEvent<MessagePayload>,
): event is MessageEvent<
  MessagePayload<
    DataServerCommand,
    DataServerCommandPayloadMap[DataServerCommand.Init]
  >
> {
  return event.data.type === DataServerCommand.Init;
}

export function isDataServerGetAllDataCommand(
  event: MessageEvent<MessagePayload>,
): event is MessageEvent<
  MessagePayload<
    DataServerCommand,
    DataServerCommandPayloadMap[DataServerCommand.GetAllData]
  >
> {
  return event.data.type === DataServerCommand.GetAllData;
}

export function isDataServerGetLogTimesCommand(
  event: MessageEvent<MessagePayload>,
): event is MessageEvent<
  MessagePayload<
    DataServerCommand,
    DataServerCommandPayloadMap[DataServerCommand.GetLogTimes]
  >
> {
  return event.data.type === DataServerCommand.GetLogTimes;
}

export function isDataServerGetPointCloudAtCommand(
  event: MessageEvent<MessagePayload>,
): event is MessageEvent<
  MessagePayload<
    DataServerCommand,
    DataServerCommandPayloadMap[DataServerCommand.GetPointCloudAt]
  >
> {
  return event.data.type === DataServerCommand.GetPointCloudAt;
}
